/**
 * Permission helpers shared by Tasks & Checklist panels.
 *
 * Super admin = permanent delete authority. Regular admins can only archive.
 *
 * Source of truth (role refactor Phase 1):
 *   - elevation (admin / super_admin) is read from the coarse `accessLevel`
 *     carried in the custom claim and mirrored onto the user object.
 *   - granular capability lives in `roles[]` (see deriveRoleFlags / roles.js).
 *
 * During the single→multi migration the user object may not yet carry
 * `accessLevel`; we fall back to deriving it from `roles[]` / the legacy single
 * `role`. The permanent-super-admin email and the legacy `superAdmin: true`
 * boolean remain emergency fallbacks, checked last, so a corrupted/missing
 * claim never locks Clint out.
 */
import {
  ACCESS_LEVELS,
  PERMANENT_SUPER_ADMIN_EMAIL,
  accessLevelForRoles,
  hasAnyRole,
} from './roles';

export const SUPER_ADMIN_EMAILS = [PERMANENT_SUPER_ADMIN_EMAIL];

// Resolve the coarse access level from the user object. Prefer the claim-backed
// `accessLevel`; otherwise derive from roles[] or the legacy single role.
function resolveAccessLevel(user) {
  if (user?.accessLevel) return user.accessLevel;
  return accessLevelForRoles(user?.roles != null ? user.roles : user?.role);
}

export function isSuperAdmin(user) {
  if (!user) return false;
  if (resolveAccessLevel(user) === ACCESS_LEVELS.SUPER_ADMIN) return true;
  // Emergency fallbacks (last resort, never the primary path)
  if (user.superAdmin === true) return true;
  const email = (user.email || '').toLowerCase();
  return SUPER_ADMIN_EMAILS.includes(email);
}

export function isAdmin(user) {
  if (!user) return false;
  const level = resolveAccessLevel(user);
  if (level === ACCESS_LEVELS.ADMIN || level === ACCESS_LEVELS.SUPER_ADMIN) return true;
  return isSuperAdmin(user);
}

// RW-670: client mirror of firestore.rules `isAdminOrTC()` — the ONLY roles
// granted a tenant-wide read of the owner-scoped `buyers` / `buyerNeeds`
// collections. Everyone else must scope list queries to their own docs
// (where createdBy/agentId/postedBy == uid) or the tightened parent read rule
// returns permission-denied. Trainer is deliberately NOT included (the rule
// does not grant it; treat any tenant-wide buyer read for trainer as a bug).
// Parallel to `canSeeAllTransactions` (transactions use the same admin+tc set
// but a different owner field — partyUids). Keep the two in lock-step with the
// rule's `isAdminOrTC()` definition.
export function isAdminOrTC(user) {
  if (!user) return false;
  if (isAdmin(user)) return true; // admin + super_admin
  return hasAnyRole(user.roles != null ? user.roles : user.role, ['tc']);
}

// Phase 5: the office-staff self-service role. Least privileged — admits the user
// ONLY to their own staff record (the StaffSelfService surface), never any admin
// surface. The server self-scope (auth_uid == caller.uid) is the real boundary;
// this client flag only routes them to the restricted page.
export function isStaffSelf(user) {
  if (!user) return false;
  return hasAnyRole(user.roles != null ? user.roles : user.role, ['staff_self']);
}
